import { DataResidency } from "@harmonyso/tenants-sdk";
import { z } from "zod";
import { AppMode } from "@/lib/config/types";
import { getStoredConfig } from "@/lib/config/stored-config";
import { PRIMARY_API_BASE_URL } from "@/lib/config/api-base";

export interface RemoteCellAuth {
  apiBaseUrl: string;
  tenantsBaseUrl: string;
  descopeProjectId: string;
  descopeTenantId: string;
  flowId: string;
}

/**
 * A deployment cell running its own tenants service. The primary cell is the
 * one the app signs in to; its calls go through the SDK's configured client.
 * Every other cell has its own API host and Descope project.
 */
export interface Cell {
  id: string;
  residency: DataResidency;
  remote?: RemoteCellAuth;
}

export type RemoteCell = Cell & { remote: RemoteCellAuth };

export const isRemoteCell = (cell: Cell): cell is RemoteCell => !!cell.remote;

export const PRIMARY_CELL: Cell = {
  id: "us-main",
  residency: DataResidency.US,
};

const trimTrailingSlashes = (url: string) => url.replace(/\/+$/, "");

const originOf = (url: string | undefined): string | undefined => {
  if (!url) {
    return undefined;
  }
  try {
    return new URL(url).origin;
  } catch {
    // A relative base URL (the Vite dev proxy) always means the primary cell.
    return undefined;
  }
};

const additionalCellSchema = z.object({
  id: z.string().min(1),
  residency: z.nativeEnum(DataResidency),
  apiBaseUrl: z
    .string()
    .url()
    .refine((url) => url.startsWith("https://"), "must use https"),
  descopeProjectId: z.string().min(1),
  descopeTenantId: z.string().min(1),
  flowId: z.string().min(1).default("sign-in-admin"),
});

/**
 * Cells from VITE_ADDITIONAL_CELLS, after the primary cell. A cell that
 * reuses the primary cell's id or API origin is dropped, because its requests
 * would be indistinguishable from the primary cell's.
 */
export function parseCells(
  additionalCellsJson: string | undefined,
  mode: AppMode,
  primaryApiBaseUrl: string,
): Cell[] {
  if (!additionalCellsJson || mode === AppMode.LOCAL) {
    return [PRIMARY_CELL];
  }

  let raw: unknown;
  try {
    raw = JSON.parse(additionalCellsJson);
  } catch (error) {
    console.error("VITE_ADDITIONAL_CELLS is not valid JSON:", error);
    return [PRIMARY_CELL];
  }

  const parsed = z.array(additionalCellSchema).safeParse(raw);
  if (!parsed.success) {
    console.error("VITE_ADDITIONAL_CELLS is invalid:", parsed.error.issues);
    return [PRIMARY_CELL];
  }

  const additional = parsed.data
    .filter(
      (cell) =>
        cell.id !== PRIMARY_CELL.id &&
        originOf(cell.apiBaseUrl) !== originOf(primaryApiBaseUrl),
    )
    .map(
      (cell): Cell => ({
        id: cell.id,
        residency: cell.residency,
        remote: {
          apiBaseUrl: trimTrailingSlashes(cell.apiBaseUrl),
          tenantsBaseUrl: `${trimTrailingSlashes(cell.apiBaseUrl)}/tenants`,
          descopeProjectId: cell.descopeProjectId,
          descopeTenantId: cell.descopeTenantId,
          flowId: cell.flowId,
        },
      }),
    );

  return [PRIMARY_CELL, ...additional];
}

export const CELLS: Cell[] = parseCells(
  import.meta.env.VITE_ADDITIONAL_CELLS,
  getStoredConfig()?.mode ?? AppMode.REMOTE,
  PRIMARY_API_BASE_URL,
);

export function findCell(cellId: string | null | undefined): Cell | undefined {
  return CELLS.find((cell) => cell.id === cellId);
}

export const isRemoteCellId = (cellId: string | null | undefined): boolean => {
  const cell = findCell(cellId);
  return !!cell && isRemoteCell(cell);
};

/** The remote cell whose API serves `baseURL`, matched by origin. */
export function remoteCellForBaseUrl(
  baseURL: string | undefined,
): RemoteCell | undefined {
  const origin = originOf(baseURL);
  if (!origin) {
    return undefined;
  }
  return CELLS.filter(isRemoteCell).find(
    (cell) => originOf(cell.remote.apiBaseUrl) === origin,
  );
}

/**
 * Per-call options that send a tenants-SDK request to the given cell. The
 * primary cell (and an unknown id) gets no override, so its requests are
 * exactly what they were before cells existed.
 */
export function cellRequestOptions(cellId: string | null | undefined): {
  baseURL?: string;
} {
  const remote = findCell(cellId)?.remote;
  return remote ? { baseURL: remote.tenantsBaseUrl } : {};
}

/** Search params that open a tenant's detail page against its own cell. */
export const tenantCellSearch = (tenant: {
  deployment_cell?: string | null;
}) => ({ cell: tenant.deployment_cell ?? undefined });

/** A tenant's name for pickers; with several cells it also names the residency. */
export const tenantLabel = (tenant: {
  company: string;
  data_residency?: string | null;
  deployment_cell?: string | null;
}): string =>
  CELLS.length > 1
    ? `${tenant.company} (${tenant.data_residency ?? tenant.deployment_cell ?? "unknown cell"})`
    : tenant.company;

/** A picker option value that stays unique when two cells have a same-named tenant. */
export const tenantOptionValue = (tenant: {
  company: string;
  id: string;
}): string =>
  CELLS.length > 1 ? `${tenant.company} ${tenant.id}` : tenant.company;

/**
 * Options for listing a cell's tenants. Another cell's list gets a timeout,
 * so a cell that hangs can't hold every tenant picker on "Loading".
 */
export function cellListRequestOptions(cellId: string | null | undefined): {
  baseURL?: string;
  timeout?: number;
} {
  const options = cellRequestOptions(cellId);
  return options.baseURL ? { ...options, timeout: 10_000 } : options;
}
